import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import { AuthContext } from '../../context/AuthContext';
import useLocalOpsAccessPlayer from '../../hooks/useLocalOpsAccessPlayer';
import { canViewLocalOpsOps } from '../../utils/localOpsOpsAccess';
import './localOpsProofsAdmin.css';

const PROOFS_ENDPOINT = 'local-ops/admin/proofs';
const MISSING_PROOFS_ENDPOINT = `${PROOFS_ENDPOINT}/missing`;
const ENSURE_PROOF_ENDPOINT = `${PROOFS_ENDPOINT}/ensure`;

const buildDefaultFilters = () => ({
  regionSlug: 'camden',
  taskType: '',
  mediaStatus: '',
  helperRecognition: '',
  search: '',
});

const formatDate = (value) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString();
};

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return `$${amount.toFixed(2)}`;
};

const normalizeProofsResponse = (response) => {
  const data = response?.data?.data || response?.data || {};
  return Array.isArray(data.proofs) ? data.proofs : [];
};

const normalizeMissingProofsResponse = (response) => {
  const data = response?.data?.data || response?.data || {};
  return Array.isArray(data.tasks) ? data.tasks : [];
};

const normalizeEnsureProofResponse = (response) =>
  response?.data?.data?.proof || response?.data?.proof || null;

const stripApi = (url) =>
  typeof url === 'string' ? url.replace(/\/api\/v1\/?$/, '') : '';

const DEFAULT_PROOF_SHARE_BASE = 'https://api.joegalaxy.net';

const getProofShareOrigin = () =>
  stripApi(process.env.REACT_APP_SHARE_BASE_URL) ||
  stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
  stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
  stripApi(process.env.REACT_APP_API_BASE_URL) ||
  DEFAULT_PROOF_SHARE_BASE;

const buildPublicProofUrl = (sharePath) => {
  if (!sharePath || typeof window === 'undefined') return '';
  return new URL(sharePath, getProofShareOrigin()).toString();
};

const CopyButton = ({ sharePath, onCopied }) => {
  const publicUrl = buildPublicProofUrl(sharePath);
  return (
    <button
      type="button"
      className="localops-proofs-admin__secondary"
      disabled={!publicUrl}
      onClick={async () => {
        if (!publicUrl) return;
        await navigator.clipboard?.writeText(publicUrl);
        onCopied?.(publicUrl);
      }}
    >
      Copy link
    </button>
  );
};

const ProofStatusPill = ({ children, tone = 'neutral' }) => (
  <span className={`localops-proofs-admin__pill is-${tone}`}>{children}</span>
);

const ProofPreview = ({ proof }) => {
  if (!proof) {
    return (
      <section className="localops-proofs-admin__preview is-empty">
        <JGLocalOpsMark />
        <h2>Select a proof</h2>
        <p>Preview safe public proof fields before copying or opening.</p>
      </section>
    );
  }

  const mediaStatus = proof.audit?.mediaStatus;
  const helperStatus = proof.audit?.helperRecognitionStatus;
  const safeMedia = Array.isArray(proof.safeMedia) ? proof.safeMedia : [];
  const heroMedia = safeMedia.find((item) => item?.url);

  return (
    <section className="localops-proofs-admin__preview">
      <div className="localops-proofs-admin__preview-head">
        <JGLocalOpsMark />
        <div>
          <p>Public proof preview</p>
          <h2>{proof.taskLabel || 'Local task completed'}</h2>
        </div>
      </div>

      <div className="localops-proofs-admin__preview-media">
        {heroMedia ? (
          heroMedia.type === 'video' ? (
            <video src={heroMedia.url} controls playsInline preload="metadata" />
          ) : (
            <img src={heroMedia.url} alt="" />
          )
        ) : (
          <div data-testid="proof-admin-fallback">
            <JGLocalOpsMark />
            <strong>Contextual fallback used</strong>
            <span>No share-safe media.</span>
          </div>
        )}
      </div>

      <div className="localops-proofs-admin__preview-copy">
        <p className="localops-proofs-admin__kicker">
          Task completed in {proof.regionLabel || 'your area'}
        </p>
        <h3>Post the task. Watch it get done.</h3>
        <p>
          {proof.helper?.handle
            ? `Completed by ${proof.helper.handle}`
            : proof.helperFallback || 'Completed by a local JG LocalOps helper'}
        </p>
      </div>

      <div className="localops-proofs-admin__meta-grid">
        <div>
          <span>Proof ID</span>
          <strong>{proof.proofId}</strong>
        </div>
        <div>
          <span>Task ID</span>
          <strong>{proof.taskId || proof.audit?.taskId || '--'}</strong>
        </div>
        <div>
          <span>Media</span>
          <strong>
            {mediaStatus?.hasSafeMedia
              ? `${mediaStatus.safeMediaCount} safe media`
              : 'Contextual fallback'}
          </strong>
        </div>
        <div>
          <span>Helper</span>
          <strong>{helperStatus?.safe ? 'Safe recognition' : 'Fallback copy'}</strong>
        </div>
        <div>
          <span>Completed</span>
          <strong>{formatDate(proof.completedAt)}</strong>
        </div>
        <div>
          <span>Created</span>
          <strong>{formatDate(proof.audit?.createdAt)}</strong>
        </div>
      </div>
    </section>
  );
};

const MissingProofsList = ({
  tasks,
  loading,
  generatingTaskId,
  onGenerate,
  onOpenProof,
  onCopied,
}) => (
  <section className="localops-proofs-admin__list">
    <div className="localops-proofs-admin__list-head">
      <div>
        <h2>Missing Proofs</h2>
        <p>Completed tasks that do not yet have a proof link.</p>
      </div>
      <span>{loading ? 'Loading...' : `${tasks.length} tasks`}</span>
    </div>
    {tasks.length === 0 && !loading ? (
      <p className="localops-proofs-admin__empty">
        No completed tasks are missing proof links for these filters.
      </p>
    ) : null}
    {tasks.map((task) => {
      const proof = task.generatedProof || task.proof || null;
      const generated = Boolean(proof?.sharePath);
      const amount = formatAmount(task.amountUSD);
      const mediaStatus = task.mediaStatus || {};
      const helperStatus = task.helperRecognitionStatus || {};
      return (
        <article
          key={task.taskId}
          className={`localops-proofs-admin__row${generated ? ' is-generated' : ''}`}
        >
          <div className="localops-proofs-admin__row-main is-static">
            <strong>{task.taskLabel || 'Local task completed'}</strong>
            <span>
              {[task.regionLabel, task.taskType, task.starterId]
                .filter(Boolean)
                .join(' · ') || 'Completed LocalOps task'}
            </span>
            <small>
              {formatDate(task.completedAt)}
              {amount ? ` · ${amount}` : ''}
            </small>
          </div>
          <div className="localops-proofs-admin__row-pills">
            <ProofStatusPill tone={generated ? 'green' : 'yellow'}>
              {generated ? 'Proof generated' : 'Missing proof'}
            </ProofStatusPill>
            <ProofStatusPill tone={mediaStatus.hasSafeMedia ? 'green' : 'yellow'}>
              {mediaStatus.hasSafeMedia ? 'Safe media available' : 'Contextual fallback'}
            </ProofStatusPill>
            <ProofStatusPill tone={helperStatus.safe ? 'green' : 'neutral'}>
              {helperStatus.safe ? 'Helper recognition available' : 'Helper fallback'}
            </ProofStatusPill>
          </div>
          <div className="localops-proofs-admin__row-actions">
            {generated ? (
              <>
                <button
                  type="button"
                  className="localops-proofs-admin__secondary"
                  onClick={() => onOpenProof(proof)}
                >
                  Open proof
                </button>
                <CopyButton sharePath={proof.sharePath} onCopied={onCopied} />
              </>
            ) : (
              <button
                type="button"
                className="localops-proofs-admin__primary"
                disabled={generatingTaskId === task.taskId}
                onClick={() => onGenerate(task)}
              >
                {generatingTaskId === task.taskId ? 'Generating...' : 'Generate proof'}
              </button>
            )}
          </div>
        </article>
      );
    })}
  </section>
);

export default function LocalOpsProofsAdmin() {
  const { player } = useContext(AuthContext);
  const { accessPlayer } = useLocalOpsAccessPlayer(player);
  const allowed = canViewLocalOpsOps(accessPlayer);
  const [activeTab, setActiveTab] = useState('proofs');
  const [filters, setFilters] = useState(() => buildDefaultFilters());
  const [proofs, setProofs] = useState([]);
  const [missingTasks, setMissingTasks] = useState([]);
  const [selectedProof, setSelectedProof] = useState(null);
  const [loading, setLoading] = useState(false);
  const [missingLoading, setMissingLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generatingTaskId, setGeneratingTaskId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${player?.token || ''}`,
      },
    }),
    [player?.token],
  );

  const fetchProofs = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    try {
      const params = Object.entries(filters).reduce((acc, [key, value]) => {
        const clean = String(value || '').trim();
        if (clean) acc[key] = clean;
        return acc;
      }, {});
      const response = await axiosConn.get(PROOFS_ENDPOINT, {
        ...authConfig,
        params,
      });
      const rows = normalizeProofsResponse(response);
      setProofs(rows);
      setSelectedProof((current) => {
        if (!current) return rows[0] || null;
        return rows.find((proof) => proof.proofId === current.proofId) || rows[0] || null;
      });
    } catch (err) {
      setError('Unable to load proof inbox.');
      setProofs([]);
      setSelectedProof(null);
    } finally {
      setLoading(false);
    }
  }, [allowed, authConfig, filters]);

  const fetchMissingProofs = useCallback(async () => {
    if (!allowed) return;
    setMissingLoading(true);
    setError('');
    try {
      const params = Object.entries(filters).reduce((acc, [key, value]) => {
        const clean = String(value || '').trim();
        if (clean) acc[key] = clean;
        return acc;
      }, {});
      const response = await axiosConn.get(MISSING_PROOFS_ENDPOINT, {
        ...authConfig,
        params,
      });
      setMissingTasks(normalizeMissingProofsResponse(response));
    } catch (err) {
      setError('Unable to load missing proof tasks.');
      setMissingTasks([]);
    } finally {
      setMissingLoading(false);
    }
  }, [allowed, authConfig, filters]);

  useEffect(() => {
    if (activeTab === 'missing') {
      fetchMissingProofs();
      return;
    }
    fetchProofs();
  }, [activeTab, fetchMissingProofs, fetchProofs]);

  const loadDetail = useCallback(
    async (proof) => {
      if (!proof?.proofId) return;
      setDetailLoading(true);
      setError('');
      try {
        const response = await axiosConn.get(
          `${PROOFS_ENDPOINT}/${encodeURIComponent(proof.proofId)}`,
          authConfig,
        );
        setSelectedProof(response?.data?.data || proof);
      } catch (err) {
        setError('Unable to load proof detail.');
        setSelectedProof(proof);
      } finally {
        setDetailLoading(false);
      }
    },
    [authConfig],
  );

  const openProof = useCallback(
    (proof) => {
      const publicUrl = buildPublicProofUrl(proof?.sharePath);
      if (publicUrl) {
        window.open(publicUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [],
  );

  const generateProofForTask = useCallback(
    async (task) => {
      if (!task?.taskId) return;
      setGeneratingTaskId(task.taskId);
      setError('');
      setNotice('');
      try {
        const response = await axiosConn.post(
          ENSURE_PROOF_ENDPOINT,
          { taskId: task.taskId },
          authConfig,
        );
        const proof = normalizeEnsureProofResponse(response);
        if (!proof?.sharePath) {
          throw new Error('Missing proof sharePath');
        }
        setMissingTasks((current) =>
          current.map((row) =>
            row.taskId === task.taskId
              ? {
                  ...row,
                  proofStatus: 'generated',
                  generatedProof: proof,
                }
              : row,
          ),
        );
        setSelectedProof(proof);
        setNotice('Proof generated.');
      } catch (err) {
        setError('Unable to generate proof for this completed task.');
      } finally {
        setGeneratingTaskId('');
      }
    },
    [authConfig],
  );

  const refreshCurrentTab = useCallback(() => {
    if (activeTab === 'missing') {
      fetchMissingProofs();
      return;
    }
    fetchProofs();
  }, [activeTab, fetchMissingProofs, fetchProofs]);

  if (!allowed) {
    return (
      <main className="localops-proofs-admin">
        <section className="localops-proofs-admin__blocked">
          <JGLocalOpsMark />
          <h1>Proofs unavailable</h1>
          <p>True admin access is required for LocalOps proof operations.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="localops-proofs-admin">
      <header className="localops-proofs-admin__header">
        <div>
          <JGLocalOpsMark />
          <p>LocalOps proof inbox</p>
          <h1>Completion Proofs</h1>
        </div>
        <button
          type="button"
          onClick={refreshCurrentTab}
          disabled={loading || missingLoading}
        >
          {loading || missingLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      <section className="localops-proofs-admin__tabs" aria-label="Proof inbox sections">
        <button
          type="button"
          className={activeTab === 'proofs' ? 'is-active' : ''}
          onClick={() => setActiveTab('proofs')}
        >
          Proofs
        </button>
        <button
          type="button"
          className={activeTab === 'missing' ? 'is-active' : ''}
          onClick={() => setActiveTab('missing')}
        >
          Missing Proofs
        </button>
      </section>

      <section className="localops-proofs-admin__filters">
        <label>
          Region
          <input
            value={filters.regionSlug}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                regionSlug: event.target.value,
              }))
            }
          />
        </label>
        <label>
          Task type
          <input
            value={filters.taskType}
            placeholder="errand, delivery..."
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                taskType: event.target.value,
              }))
            }
          />
        </label>
        <label>
          Media
          <select
            value={filters.mediaStatus}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                mediaStatus: event.target.value,
              }))
            }
          >
            <option value="">Any</option>
            <option value="safe_media">Safe media</option>
            <option value="contextual_fallback">Fallback</option>
          </select>
        </label>
        <label>
          Helper
          <select
            value={filters.helperRecognition}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                helperRecognition: event.target.value,
              }))
            }
          >
            <option value="">Any</option>
            <option value="safe">Safe recognition</option>
            <option value="fallback">Fallback copy</option>
          </select>
        </label>
        <label>
          Search
          <input
            value={filters.search}
            placeholder="proofId, taskId, title"
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                search: event.target.value,
              }))
            }
          />
        </label>
      </section>

      {notice ? <div className="localops-proofs-admin__notice">{notice}</div> : null}
      {error ? <div className="localops-proofs-admin__error">{error}</div> : null}

      <div className="localops-proofs-admin__layout">
        {activeTab === 'missing' ? (
          <MissingProofsList
            tasks={missingTasks}
            loading={missingLoading}
            generatingTaskId={generatingTaskId}
            onGenerate={generateProofForTask}
            onOpenProof={openProof}
            onCopied={() => setNotice('Public proof link copied.')}
          />
        ) : (
          <section className="localops-proofs-admin__list">
            <div className="localops-proofs-admin__list-head">
              <h2>Proof snapshots</h2>
              <span>{loading ? 'Loading...' : `${proofs.length} proofs`}</span>
            </div>
            {proofs.length === 0 && !loading ? (
              <p className="localops-proofs-admin__empty">
                No proof snapshots match these filters.
              </p>
            ) : null}
            {proofs.map((proof) => {
              const selected = selectedProof?.proofId === proof.proofId;
              const mediaStatus = proof.audit?.mediaStatus;
              const helperStatus = proof.audit?.helperRecognitionStatus;
              return (
                <article
                  key={proof.proofId}
                  className={`localops-proofs-admin__row${selected ? ' is-selected' : ''}`}
                >
                  <button
                    type="button"
                    className="localops-proofs-admin__row-main"
                    onClick={() => loadDetail(proof)}
                  >
                    <strong>{proof.taskLabel || 'Local task completed'}</strong>
                    <span>{proof.regionLabel || 'Unknown region'}</span>
                    <small>{formatDate(proof.completedAt)}</small>
                  </button>
                  <div className="localops-proofs-admin__row-pills">
                    <ProofStatusPill tone={mediaStatus?.hasSafeMedia ? 'green' : 'yellow'}>
                      {mediaStatus?.hasSafeMedia ? 'safe media' : 'fallback'}
                    </ProofStatusPill>
                    <ProofStatusPill tone={helperStatus?.safe ? 'green' : 'neutral'}>
                      {helperStatus?.safe ? 'helper safe' : 'helper fallback'}
                    </ProofStatusPill>
                  </div>
                  <div className="localops-proofs-admin__row-actions">
                    <button
                      type="button"
                      className="localops-proofs-admin__secondary"
                      onClick={() => openProof(proof)}
                    >
                      Open proof
                    </button>
                    <CopyButton
                      sharePath={proof.sharePath}
                      onCopied={() => setNotice('Public proof link copied.')}
                    />
                  </div>
                </article>
              );
            })}
          </section>
        )}

        <div>
          {detailLoading ? (
            <section className="localops-proofs-admin__preview is-empty">
              <JGLocalOpsMark />
              <p>Loading proof detail...</p>
            </section>
          ) : (
            <ProofPreview proof={selectedProof} />
          )}
        </div>
      </div>
    </main>
  );
}
